import { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { QuestionCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { useGetBaseVariant } from '../../documents_v2/hooks/useGetBaseVariant';
import { TagRow } from './TagRow';

/**
 * Shared variant table used by the tag modals. Renders one TagRow per variant.
 *
 * @param {number} documentId
 * @param {import('models/documents.models').Tag} formData current form data
 * @param {Array} variants already filtered list of variants to render
 * @param {{variant: string, text: string, link: string, title: string, reference: string}} columnTitles translated column titles
 * @param {function(*, string, number):void} handleFormChange
 * @param {function(boolean, object):void} toggleVariantItem
 * @param {function(object):void} copyItem
 * @param {object} defaultItem values used for new/empty items
 */
export function DocumentTagForm({
    documentId,
    formData,
    variants,
    columnTitles,
    handleFormChange,
    toggleVariantItem,
    copyItem,
    defaultItem,
}) {
    const baseVariant = useGetBaseVariant();
    const { tagItems = [], isOverride } = formData;
    const { t } = useTranslation('documents');

    // Map for O(1) item lookup per row instead of a linear find per row.
    const itemsByVariant = useMemo(() => {
        const map = new Map();

        tagItems.forEach((item) => map.set(item.documentVariantId, item));

        return map;
    }, [tagItems]);

    return (
        <Container fluid>
            <Row className="no-gutters font-weight-bold text-secondary mb-3">
                <Col sm={2}>{columnTitles.variant}</Col>
                <Col className="pr-3">
                    <div className="d-flex align-items-baseline">
                        {columnTitles.text}
                        <QuestionCircle
                            data-uk-tooltip={t('documents:document.navbar.tags.editModal.modal.textTooltip')}
                            size={14}
                            className="text-secondary ml-1"
                        />
                    </div>
                </Col>
                <Col className="pr-3">{columnTitles.link}</Col>
                <Col className="pr-3">
                    {columnTitles.title}
                    <QuestionCircle
                        data-uk-tooltip={t('documents:document.navbar.tags.editModal.modal.titleTooltip')}
                        size={14}
                        className="text-secondary ml-1"
                    />
                </Col>
                <Col>{columnTitles.reference}</Col>
            </Row>

            {variants.map((variant, index) => (
                <TagRow
                    item={itemsByVariant.get(variant.id)}
                    variant={variant}
                    isOverride={isOverride}
                    handleFormChange={handleFormChange}
                    defaultItem={defaultItem}
                    documentId={documentId}
                    toggleVariantItem={toggleVariantItem}
                    copyItem={copyItem}
                    baseVariantId={baseVariant.id}
                    key={`tag-${index}-variant-${variant.id}`}
                />
            ))}
        </Container>
    );
}
